import { useEffect, useMemo, useRef } from 'react'
import { UseFormWatch } from 'react-hook-form'

import { VariableValue } from '@components/Variables/types'

import { getValuesFromVariables } from '@helpers'
import { ACTION_CODE } from '@constants'
import { ActionRow, Row } from '@types'

import { EVENT_NAME, publish } from '../events'

export const useChangeVariableWithAction = (
  availableActions: ActionRow[] | undefined,
  outerVariables: VariableValue[],
  watch: UseFormWatch<Row>,
  defaultValues: Row
) => {
  const isInitFieldsValuesRef = useRef(false)

  const actionsWithChangeVariables = useMemo(() => {
    return availableActions
      ? availableActions.filter(action => {
          return action.actionCode === ACTION_CODE.CHANGE_VARIABLE_VALUE
        })
      : []
  }, [availableActions])

  const trackingFieldNames = useMemo(() => {
    return actionsWithChangeVariables.map(action => {
      return action.objectFieldCode
    })
  }, [actionsWithChangeVariables])

  const handleChangeVariable = (fieldsArray, fieldName: string) => {
    const currentTime = new Date().toISOString()
    const targetAction = actionsWithChangeVariables.find(action => {
      return action.objectFieldCode === fieldName
    })
    const newFieldValue = fieldsArray[fieldName]

    const srcObj = {
      dropdownValue: newFieldValue,
      outerVariables: getValuesFromVariables(outerVariables),
    }

    const objectValue = targetAction?.jsValue
      ? new Function('srcObj', targetAction?.jsValue)(srcObj)
      : newFieldValue?.id

    const flattenNewFieldValue = typeof newFieldValue === 'object' ? objectValue : newFieldValue

    const newVariableValue = {
      ...targetAction?.variable,
      value: flattenNewFieldValue,
      updatedOn: currentTime,
    }

    setTimeout(() => {
      publish(EVENT_NAME.CHANGE_VARIABLE_VALUE, newVariableValue)
    }, 0)
  }

  useEffect(() => {
    if (isInitFieldsValuesRef.current) return

    if (Object.keys(defaultValues).length === 0) return

    isInitFieldsValuesRef.current = true

    trackingFieldNames.forEach(fieldName => {
      if (fieldName in defaultValues) {
        handleChangeVariable(defaultValues, fieldName)
      }
    })
  }, [trackingFieldNames, defaultValues])

  useEffect(() => {
    if (!trackingFieldNames.length) return

    const subscription = watch((data, { name }) => {
      if (name && trackingFieldNames.includes(name)) {
        handleChangeVariable(data, name)
      }
    })

    return () => subscription.unsubscribe()
  }, [watch, trackingFieldNames])
}
